import React, { useState, useEffect } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import {
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    SearchOutlined,
    BookOutlined
  } from '@ant-design/icons';
import { Table, Button, Modal, Space, Card, Input } from 'antd';
import SubjectModal from './SubjectModal';
import ConfirmationModal from './ConfirmationModal';

function EditDeleteSubject() {
  const { t } = useTranslation();
  const [subjects, setSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [currentSubject, setCurrentSubject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setError] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchSubjects();
  }, []);
  
  useEffect(() => {
    handleSearch(searchText);
  }, [subjects, searchText]);

  const fetchSubjects = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/subjects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sortedSubjects = response.data.sort((a, b) => a.code.localeCompare(b.code));
      setSubjects(sortedSubjects);
      setFilteredSubjects(sortedSubjects);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setError(t('fetchSubjectsError'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = (value) => {
    const searchValue = value.toLowerCase();
    const filtered = subjects.filter(subject => 
      subject.code.toLowerCase().includes(searchValue) ||
      subject.title.toLowerCase().includes(searchValue) ||
      subject.subjectgroup.toLowerCase().includes(searchValue) ||
      (subject.description && subject.description.toLowerCase().includes(searchValue)) ||
      (subject.specification && subject.specification.toLowerCase().includes(searchValue))
    );
    setFilteredSubjects(filtered);
  };

  const handleEdit = (subject) => {
    setCurrentSubject(subject);
    setShowSubjectModal(true);
  };

  const handleDelete = (subjectId) => {
    Modal.confirm({
      title: t('confirmDelete'),
      content: t('areYouSureToDelete'),
      okText: t('yes'),
      cancelText: t('no'),
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/subjects/${subjectId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setSubjects(prev => prev.filter(subject => subject.subjectid !== subjectId));
          setConfirmationMessage(t('subjectDeleted'));
          setShowConfirmationModal(false);
        } catch (error) {
          console.error('Error deleting subject:', error);
          setError(t('deleteSubjectError'));
        }
      }
    });
  };

  const handleSaveSubject = async (subjectData) => {
    try {
      const token = localStorage.getItem('token');
      let response;
      if (currentSubject) {
        response = await axios.put(`${API_URL}/subjects/${currentSubject.subjectid}`, subjectData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubjects(prev => prev.map(subject => subject.subjectid === response.data.subjectid ? response.data : subject));
        setConfirmationMessage(t('subjectUpdated'));
      } else {
        response = await axios.post(`${API_URL}/subjects`, subjectData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubjects(prev => [...prev, response.data]);
        setConfirmationMessage(t('subjectCreated'));
      }
      setShowSubjectModal(false);
      setShowConfirmationModal(false);
    } catch (error) {
      console.error('Error saving subject:', error);
      setError(t('saveSubjectError'));
    }
  };

  const columns = [
    {
      title: t('code'),
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: t('group'),
      dataIndex: 'subjectgroup',
      key: 'subjectgroup',
    },
    {
      title: t('subjecttitle'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('level'),
      dataIndex: 'level',
      key: 'level',
    },
    {
      title: t('specification'),
      dataIndex: 'specification',
      key: 'specification',
    },
    {
      title: t('description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button onClick={() => handleEdit(record)} icon={<EditOutlined />} />
          <Button onClick={() => handleDelete(record.subjectid)} icon={<DeleteOutlined />} danger />
        </Space>
      ),
    },
  ];

  return (
    <div>
              <Button
          type="primary"
          icon={<BookOutlined />}
          onClick={() => {
            setCurrentSubject(null);
            setShowSubjectModal(true);
          }}
          style={{ marginRight: 16 }}
        >
          {t('createSubject')}
        </Button>

    <Card style={{ marginTop: '16px'}}>
     <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
        </div>
        <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <Input
          placeholder={t('Search Subjects')}
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300, marginRight: 16 }}
          allowClear
        />
        <Button
          type="default"
          icon={<ReloadOutlined />}
          onClick={fetchSubjects}
          style={{ marginRight: 16 }}
        >
          Refresh
        </Button>
        </div>
      

      <Table
        columns={columns}
        dataSource={filteredSubjects}
        rowKey="subjectid"
        loading={loading}
        pagination={{ pageSize: 150 }}
      />

      <SubjectModal
        isOpen={showSubjectModal}
        onClose={() => setShowSubjectModal(false)}
        onSave={handleSaveSubject}
        subjectData={currentSubject}
      />

      <ConfirmationModal
        isOpen={showConfirmationModal}
        message={confirmationMessage}
        onClose={() => setShowConfirmationModal(false)}
      />

    </Card>
        </div>
  );
}

export default withRoleAccess(EditDeleteSubject, ['admin', 'superadmin']);